// Admin API client. Every call needs a bearer access token from useAuth().
// The backend gates these on RequireAuth + RequireAdmin so a non-admin
// token returns 403.

async function handle<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let msg = `${res.status}`;
    let code = '';
    try {
      const body = await res.json();
      msg = body?.error?.message || msg;
      code = body?.error?.code || '';
    } catch {}
    const e: any = new Error(msg);
    e.code = code;
    e.status = res.status;
    throw e;
  }
  return res.json() as Promise<T>;
}

function authHeaders(accessToken: string): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${accessToken}`,
  };
}

export type MatchSummary = {
  number: number;
  matchday: number;
  phase: string;
  group?: string;
  stadium: string;
  kickoff_utc: string;
  team_a?: string;
  team_b?: string;
  team_a_slot?: string;
  team_b_slot?: string;
};

/** Read the admin's live match list from the DB.
 *
 * The original implementation hit a static JSON bundle (`/data/wc2026.json`)
 * generated at build time. That hid mid-flight edits — refresh would
 * correct kickoffs in MySQL but admins kept seeing the bundled value.
 * Now backed by `GET /api/admin/matches` which reads the live rows. */
export async function loadFixtures(accessToken: string): Promise<MatchSummary[]> {
  const res = await fetch('/api/admin/matches', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ matches: MatchSummary[] }>(res);
  return body.matches ?? [];
}

export type MatchResultPayload = {
  scoreA: number;
  scoreB: number;
  winnerCode?: string;
};

export type MatchResultResponse = {
  matchNumber: number;
  scoreA: number;
  scoreB: number;
  scored: number;
};

export async function putMatchResult(
  accessToken: string,
  matchNumber: number,
  payload: MatchResultPayload,
): Promise<MatchResultResponse> {
  const res = await fetch(`/api/admin/matches/${matchNumber}/result`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<MatchResultResponse>(res);
}

export type MatchEditPayload = {
  teamA?: string;
  teamB?: string;
  stadium?: string;
  kickoffUtc?: string; // RFC3339 in UTC
};

export type MatchEditResponse = {
  matchNumber: number;
  teamA?: string;
  teamB?: string;
  stadium: string;
  kickoffUtc: string;
};

export async function putMatchEdit(
  accessToken: string,
  matchNumber: number,
  payload: MatchEditPayload,
): Promise<MatchEditResponse> {
  const res = await fetch(`/api/admin/matches/${matchNumber}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<MatchEditResponse>(res);
}

export type TeamDirectory = {
  code: string;
  name: string;
};
export type StadiumDirectory = {
  code: string;
  name: string;
  city: string;
};

/** Pull the team + stadium directories out of the WC2026 bundle (mirrored
 * at /data/wc2026.json by `make export-static`). Used by the match edit
 * modal for its dropdowns. */
export async function loadDirectories(): Promise<{ teams: TeamDirectory[]; stadiums: StadiumDirectory[] }> {
  const res = await fetch('/data/wc2026.json');
  if (!res.ok) throw new Error('fixtures bundle not available');
  const body = await res.json();
  const teams: TeamDirectory[] = Object.values(body.teams as Record<string, any>)
    .map((t) => ({ code: t.code, name: t.name?.en ?? t.code }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const stadiums: StadiumDirectory[] = Object.values(body.stadiums as Record<string, any>)
    .map((s) => ({ code: s.code, name: s.name, city: s.city }))
    .sort((a, b) => a.city.localeCompare(b.city));
  return { teams, stadiums };
}

export type PredictionsVisibility =
  | 'always'
  | 'after_match_done'
  | 'auto_hide_in_knockouts';

export type TournamentView = {
  code: string;
  name: string;
  startsAt: string;
  endsAt: string;
  status: 'upcoming' | 'live' | 'done';
  predictionsVisibility: PredictionsVisibility;
  /// Headline pool: committed + pending litres pledged across all
  /// partners. Derived live on the backend, UNLESS prizeLitresOverride
  /// is set — then it's the forced value. Read-only from clients.
  prizeLitres: number | null;
  /// Admin-forced headline value. When non-null it overrides the live
  /// derived total in prizeLitres; null means "no override, derive live".
  prizeLitresOverride: number | null;
  committedLitres: number;
  pendingLitres: number;
  redeemedLitres?: number;
  /// Client-side concealment toggle: when false, the app's prize-pool
  /// strip swaps the numbers for a "litres incoming" placeholder.
  prizeCounterVisible: boolean;
  /// End-to-end partner reveal gate: when false, the app hides the
  /// prize-pool strip entirely and replaces the partners screen with
  /// a "soon to be announced" banner.
  partnersRevealed: boolean;
};

export async function getTournament(code: string): Promise<TournamentView> {
  const res = await fetch(`/api/tournaments/${code}`);
  return handle<TournamentView>(res);
}

export type TournamentUpdate = {
  status?: 'upcoming' | 'live' | 'done';
  predictionsVisibility?: PredictionsVisibility;
  prizeCounterVisible?: boolean;
  partnersRevealed?: boolean;
  /// Tri-state: omit to leave the override unchanged, send null to clear
  /// it (counter goes back to the live derived total), or a number to
  /// force that headline value.
  prizeLitresOverride?: number | null;
};

export async function putTournament(
  accessToken: string,
  code: string,
  payload: TournamentUpdate,
): Promise<TournamentView> {
  const res = await fetch(`/api/admin/tournaments/${code}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<TournamentView>(res);
}

export type ScoringRules = {
  outcome: number;
  homeGoals: number;
  awayGoals: number;
  diff: number;
  exact: number;
  boostMultiplier: number;
  boostTokens: number;
};

export async function getRules(): Promise<ScoringRules> {
  const res = await fetch(`/api/rules`);
  return handle<ScoringRules>(res);
}

export async function putRules(
  accessToken: string,
  code: string,
  payload: Partial<ScoringRules>,
): Promise<ScoringRules> {
  const res = await fetch(`/api/admin/tournaments/${code}/rules`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<ScoringRules>(res);
}

export type AdminLeague = {
  id: string;
  name: string;
  emoji?: string;
  ownerUserId?: string;
  members: number;
  outcome: number;
  homeGoals: number;
  awayGoals: number;
  diff: number;
  exact: number;
};

export type AdminLeagueUpdate = {
  name?: string;
  emoji?: string;
  outcome?: number;
  homeGoals?: number;
  awayGoals?: number;
  diff?: number;
  exact?: number;
};

export async function listAdminLeagues(
  accessToken: string,
  q: string,
): Promise<AdminLeague[]> {
  const url = new URL('/api/admin/leagues', window.location.origin);
  if (q.trim()) url.searchParams.set('q', q.trim());
  const res = await fetch(url.pathname + url.search, { headers: authHeaders(accessToken) });
  const body = await handle<{ leagues: AdminLeague[] }>(res);
  return body.leagues ?? [];
}

export async function updateAdminLeague(
  accessToken: string,
  id: string,
  payload: AdminLeagueUpdate,
): Promise<AdminLeague> {
  const res = await fetch(`/api/admin/leagues/${id}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<AdminLeague>(res);
}

export async function deleteAdminLeague(accessToken: string, id: string): Promise<void> {
  const res = await fetch(`/api/admin/leagues/${id}`, {
    method: 'DELETE',
    headers: authHeaders(accessToken),
  });
  await handle<unknown>(res);
}

export type AdminLeagueLeaderboardEntry = {
  rank: number;
  userId: string;
  nickname: string;
  cap?: string;
  avatarUrl?: string;
  canton?: string;
  pts: number;
  picks: number;
};

export type AdminLeagueLeaderboard = {
  leagueId: string;
  total: number;
  entries: AdminLeagueLeaderboardEntry[];
};

export async function getAdminLeagueLeaderboard(
  accessToken: string,
  id: string,
): Promise<AdminLeagueLeaderboard> {
  const res = await fetch(`/api/admin/leagues/${id}/leaderboard`, {
    headers: authHeaders(accessToken),
  });
  return handle<AdminLeagueLeaderboard>(res);
}

export type RescoreMemberDelta = {
  userId: string;
  nickname: string;
  oldPts: number;
  newPts: number;
  delta: number;
};

export type RescoreResult = {
  leagueId: string;
  dryRun: boolean;
  applied: boolean;
  predictionsChanged: number;
  membersChanged: number;
  members: RescoreMemberDelta[];
};

export async function rescoreAdminLeague(
  accessToken: string,
  id: string,
  opts: { dryRun: boolean },
): Promise<RescoreResult> {
  const qs = opts.dryRun ? '?dryRun=1' : '';
  const res = await fetch(`/api/admin/leagues/${id}/rescore${qs}`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<RescoreResult>(res);
}

export type Tier = 'platinum' | 'gold' | 'silver' | 'bronze';

/// One weekly opening slot for a partner venue, mirroring the backend.
/// Weekday is 0 = Monday … 6 = Sunday so the natural sort order matches
/// the Mon-first calendar everywhere. Times are "HH:MM" in Europe/
/// Zurich. A slot wraps into the following day when closesAt <= opensAt
/// (e.g. 22:00–02:00 is one row, not two).
export type PartnerHour = {
  weekday: number;
  opensAt: string;
  closesAt: string;
};

export type Partner = {
  id: number;
  slug: string;
  name: string;
  logoPath?: string;
  whiteBg?: boolean;
  url?: string;
  tier: Tier;
  canton?: string;
  address?: string;
  lat?: number;
  lng?: number;
  contributionLitres?: number;
  redemptionCode?: string;
  remainingLitres?: number;
  servesThird: boolean;
  servesHalf: boolean;
  servesPint: boolean;
  notes?: string;
  active: boolean;
  infoLocked: boolean;
  infoLockedAt?: string;
  ownerEmail?: string;
  claimedAt?: string;
  // Always present on responses (server emits []), so we don't make it
  // optional — simpler downstream code.
  hours: PartnerHour[];
  openNow?: boolean;
  nextChangeAt?: string;
};

export type PartnerPayload = {
  slug?: string;
  name: string;
  logoPath?: string;
  whiteBg?: boolean;
  url?: string;
  tier: Tier;
  canton?: string;
  address?: string;
  lat?: number | null;
  lng?: number | null;
  contributionLitres?: number | null;
  redemptionCode?: string | null;
  remainingLitres?: number | null;
  notes?: string;
  active?: boolean;
  infoLocked?: boolean;
  ownerEmail?: string;
  // Omit to leave the schedule alone; empty array clears all hours.
  hours?: PartnerHour[];
};

export type DrinkSize = 'third' | 'half' | 'pint';

export type PrizeSlot = {
  drinkSize: DrinkSize;
  count: number;
};

export async function getPrizeSlots(
  accessToken: string,
  matchNumber: number,
): Promise<PrizeSlot[]> {
  const res = await fetch(`/api/admin/matches/${matchNumber}/prize-slots`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ slots: PrizeSlot[] }>(res);
  return body.slots ?? [];
}

export async function putPrizeSlots(
  accessToken: string,
  matchNumber: number,
  slots: PrizeSlot[],
): Promise<PrizeSlot[]> {
  const res = await fetch(`/api/admin/matches/${matchNumber}/prize-slots`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ slots }),
  });
  const body = await handle<{ slots: PrizeSlot[] }>(res);
  return body.slots ?? [];
}

export type TvLink = {
  id?: number;
  channel: string;
  region?: string;
  language?: string;
  note?: string;
  url?: string;
  isFree: boolean;
  sortOrder?: number;
};

export async function getTvLinks(
  accessToken: string,
  matchNumber: number,
): Promise<TvLink[]> {
  const res = await fetch(`/api/admin/matches/${matchNumber}/tv-links`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ links: TvLink[] }>(res);
  return body.links ?? [];
}

export async function putTvLinks(
  accessToken: string,
  matchNumber: number,
  links: TvLink[],
): Promise<TvLink[]> {
  const res = await fetch(`/api/admin/matches/${matchNumber}/tv-links`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ links }),
  });
  const body = await handle<{ links: TvLink[] }>(res);
  return body.links ?? [];
}

export async function listPartnersAdmin(accessToken: string): Promise<Partner[]> {
  const res = await fetch('/api/admin/partners', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ partners: Partner[] }>(res);
  return body.partners ?? [];
}

export async function createPartner(accessToken: string, payload: PartnerPayload): Promise<Partner> {
  const res = await fetch('/api/admin/partners', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Partner>(res);
}

export async function updatePartner(accessToken: string, id: number, payload: PartnerPayload): Promise<Partner> {
  const res = await fetch(`/api/admin/partners/${id}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Partner>(res);
}

export async function deletePartner(accessToken: string, id: number): Promise<void> {
  const res = await fetch(`/api/admin/partners/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(`${res.status}`);
  }
}

export type UploadResponse = {
  url: string;
  path: string;
  size: number;
  mime: string;
};

// ─── Admin: users ────────────────────────────────────────────────────────

export type UserRole = 'user' | 'partner' | 'admin';
export type UserStatus = 'active' | 'suspended' | 'deleted';
export type Locale = 'en' | 'de' | 'fr' | 'it';

export type AdminUser = {
  id: string;
  email: string;
  emailVerified: boolean;
  avatarUrl?: string;
  locale: Locale;
  role: UserRole;
  status: UserStatus;
  nickname?: string;
  canton?: string;
  partnerId?: number;
  partnerName?: string;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string;
};

export type AdminUsersFilter = {
  search?: string;
  role?: UserRole | '';
  status?: UserStatus | '';
  canton?: string;
  limit?: number;
  offset?: number;
};

export type AdminUsersResponse = {
  users: AdminUser[];
  total: number;
  limit: number;
  offset: number;
};

export async function listAdminUsers(
  accessToken: string,
  filter: AdminUsersFilter = {},
): Promise<AdminUsersResponse> {
  const qs = new URLSearchParams();
  if (filter.search) qs.set('search', filter.search);
  if (filter.role) qs.set('role', filter.role);
  if (filter.status) qs.set('status', filter.status);
  if (filter.canton) qs.set('canton', filter.canton);
  if (filter.limit != null) qs.set('limit', String(filter.limit));
  if (filter.offset != null) qs.set('offset', String(filter.offset));
  const url = `/api/admin/users${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<AdminUsersResponse>(res);
}

export type AdminUserUpdate = {
  role?: UserRole;
  status?: UserStatus;
  locale?: Locale;
  nickname?: string;
  canton?: string;
  // Sentinel matching the backend: omitted = unchanged, 0 = clear the
  // link, positive = link to that partner id.
  partnerId?: number | null;
};

export async function updateAdminUser(
  accessToken: string,
  id: string,
  payload: AdminUserUpdate,
): Promise<AdminUser> {
  // The backend treats `partnerId === null` as "clear the link" via the
  // 0-sentinel, and an omitted field as "unchanged". Translate here so the
  // page can use a clean nullable type.
  const body: Record<string, unknown> = { ...payload };
  if (payload.partnerId === null) body.partnerId = 0;
  const res = await fetch(`/api/admin/users/${id}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(body),
  });
  return handle<AdminUser>(res);
}

// ─── Admin: vouchers (gift drink + erase/undo for test cleanup) ─────────

export type VoucherState =
  | 'issued'
  | 'awaiting_partner'
  | 'ready_for_pickup'
  | 'delivered'
  | 'redeemed'
  | 'locked'
  | 'void'
  | 'erased';

export type AdminVoucher = {
  id: number;
  kind: 'drink' | 'article';
  state: VoucherState;
  issuedAt: string;
  redeemedAt?: string;
  fulfilledAt?: string;
  partnerName?: string;
  partnerNote?: string;
  qrToken?: string;
  matchId?: number;
  matchNumber?: number;
  drinkSize?: DrinkSize;
  label?: string;
  litres?: number;
  articleId?: number;
  articleTitle?: string;
};

export type GiftDrinkPayload = {
  tournamentCode: string;
  drinkSize: DrinkSize;
  // Optional. Gift without a match if omitted — lets you gift multiple
  // drinks to the same user without colliding with the per-match limit.
  matchNumber?: number;
};

/** POST /api/admin/users/{userId}/gift-drink */
export async function giftDrinkToUser(
  accessToken: string,
  userId: string,
  payload: GiftDrinkPayload,
): Promise<AdminVoucher> {
  const res = await fetch(`/api/admin/users/${userId}/gift-drink`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<AdminVoucher>(res);
}

/** GET /api/admin/users/{userId}/vouchers — includes erased rows. */
export async function listUserVouchers(
  accessToken: string,
  userId: string,
): Promise<AdminVoucher[]> {
  const res = await fetch(`/api/admin/users/${userId}/vouchers`, {
    headers: authHeaders(accessToken),
  });
  const body = await handle<{ vouchers: AdminVoucher[] }>(res);
  return body.vouchers ?? [];
}

/** POST /api/admin/vouchers/{id}/erase */
export async function eraseVoucher(
  accessToken: string,
  voucherId: number,
): Promise<AdminVoucher> {
  const res = await fetch(`/api/admin/vouchers/${voucherId}/erase`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<AdminVoucher>(res);
}

/** POST /api/admin/vouchers/{id}/unerase */
export async function unEraseVoucher(
  accessToken: string,
  voucherId: number,
): Promise<AdminVoucher> {
  const res = await fetch(`/api/admin/vouchers/${voucherId}/unerase`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<AdminVoucher>(res);
}

// ─── Match-sync bootstrap (admin one-shot) ───────────────────────────────

export type BootstrapResult = {
  leagueId: number;
  season: number;
  dryRun: boolean;
  teams: { linked: number; unresolvedFromApi: number };
  fixtures: { linked: number; skipped: number; ambiguous: number };
};

// Runs api-football team + fixture linkage server-side. Idempotent —
// only fills NULL rows, so safe to re-run after the WC draw. Pass
// dryRun=true to preview without writing.
export async function bootstrapSync(
  accessToken: string,
  dryRun: boolean,
): Promise<BootstrapResult> {
  const qs = dryRun ? '?dry=1' : '';
  const res = await fetch(`/api/admin/sync/bootstrap${qs}`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<BootstrapResult>(res);
}

export type RefreshSummary = {
  // Non-empty when the refresh was a no-op — currently only
  // "manually_overridden" (the match has a manual result lock).
  skipped?: string;
  reqs: number;
  liveMatches: number;
  matchesUpdated: number;
  eventsInserted: number;
  eventsUpdated: number;
  eventsRetracted: number;
  statsUpdated: number;
  lineupsFetched: number;
  errors?: string[];
};

// Force-pull one match from api-football: score + status + events + stats
// + lineups in a single shot. If the pull flips the match to 'done', the
// backend automatically recalculates tournament points, all league points
// and the prize-voucher draw, and resolves any knockout slots that fed off
// this match. No-op (skipped='manually_overridden') when the match was set
// manually — clear the override-lock first to let the sync take over.
export async function refreshMatch(
  accessToken: string,
  matchNumber: number,
): Promise<RefreshSummary> {
  const res = await fetch(`/api/admin/matches/${matchNumber}/refresh`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<RefreshSummary>(res);
}

// Realigns every linked WC2026 fixture with api-football's current
// state (kickoff time + status). Skips manually-overridden matches.
// Counterpart to the every-30-min cron, but on demand.
export async function refreshFixtures(accessToken: string): Promise<RefreshSummary> {
  const res = await fetch('/api/admin/sync/refresh-fixtures', {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<RefreshSummary>(res);
}

export interface ReresolveBracketResult {
  cleared: number;
  refilled: number;
  unlinked: number;
}

// Repairs knockout slots filled from a previous (wrong) bracket pairing.
// Run once after a bracket-table fix, then "Sync upcoming" to re-link times.
export async function reresolveBracket(accessToken: string): Promise<ReresolveBracketResult> {
  const res = await fetch('/api/admin/sync/reresolve-bracket', {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<ReresolveBracketResult>(res);
}

export type SyncUpcomingResult = {
  bootstrap: BootstrapResult;
  refresh: RefreshSummary;
};

// One-click sync: bootstrap (link any new fixtures / teams) + refresh
// (correct kickoff/status drift). Use this for everyday "make our DB
// match api-football for matches not yet played".
export async function syncUpcoming(accessToken: string): Promise<SyncUpcomingResult> {
  const res = await fetch('/api/admin/sync/upcoming', {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<SyncUpcomingResult>(res);
}

// ─── Partner self-service ────────────────────────────────────────────────

export type MyPartner = Partner;

export type MyPartnerUpdate = {
  name: string;
  url?: string;
  logoPath?: string;
  whiteBg?: boolean;
  address?: string;
  lat?: number | null;
  lng?: number | null;
  redemptionCode?: string;
  remainingLitres?: number | null;
  servesThird?: boolean;
  servesHalf?: boolean;
  servesPint?: boolean;
  // Omit to leave the schedule alone; empty array clears all hours.
  // Not gated by infoLocked — hours are operational (seasonal terrace,
  // etc.) and partners can edit them even on locked rows.
  hours?: PartnerHour[];
};

// `as` is the admin impersonation hatch: when the caller is an admin and
// passes a positive partner id, the backend resolves the request against
// that partner instead of the caller's own linked one. Plain partners
// MUST NOT pass it — the server ignores `as` for non-admins, but we
// still keep it admin-only on the call sites so the wire is clean.
function withAs(path: string, as?: number): string {
  if (as == null) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}as=${as}`;
}

export async function getMyPartner(accessToken: string, as?: number): Promise<MyPartner> {
  const res = await fetch(withAs('/api/me/partner', as), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<MyPartner>(res);
}

export async function updateMyPartner(
  accessToken: string,
  payload: MyPartnerUpdate,
  as?: number,
): Promise<MyPartner> {
  const res = await fetch(withAs('/api/me/partner', as), {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<MyPartner>(res);
}

// ─── Articles ────────────────────────────────────────────────────────────

export type ArticleKind = 'drink' | 'experience' | 'ticket' | 'gadget' | 'other';
export type ArticleDelivery = 'cantonal' | 'national' | 'digital' | 'national_pickup';

export type Article = {
  id: number;
  partnerId?: number;
  partnerName?: string;
  kind: ArticleKind;
  title: string;
  description?: string;
  imagePath?: string;
  valueChf?: number;
  stock?: number;
  delivery: ArticleDelivery;
  active: boolean;
};

export type ArticlePayload = {
  kind?: ArticleKind;
  title?: string;
  description?: string;
  imagePath?: string;
  valueChf?: number | null;
  stock?: number | null;
  delivery?: ArticleDelivery;
  active?: boolean;
};

// Admin: list every article, optionally filtered.
export async function listAdminArticles(
  accessToken: string,
  filter: { partnerId?: number | 'beercup'; kind?: ArticleKind; activeOnly?: boolean } = {},
): Promise<Article[]> {
  const qs = new URLSearchParams();
  if (filter.partnerId === 'beercup') qs.set('partnerId', '0');
  else if (typeof filter.partnerId === 'number') qs.set('partnerId', String(filter.partnerId));
  if (filter.kind) qs.set('kind', filter.kind);
  if (filter.activeOnly) qs.set('active', '1');
  const url = `/api/admin/articles${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ articles: Article[] }>(res);
  return body.articles ?? [];
}

export async function createAdminArticle(
  accessToken: string,
  payload: ArticlePayload & { partnerId?: number }, // omit/0 = BeerCup-authored
): Promise<Article> {
  const res = await fetch('/api/admin/articles', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ partnerId: payload.partnerId ?? 0, ...payload }),
  });
  return handle<Article>(res);
}

export async function updateAdminArticle(
  accessToken: string,
  id: number,
  payload: ArticlePayload,
): Promise<Article> {
  const res = await fetch(`/api/admin/articles/${id}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Article>(res);
}

export async function deleteAdminArticle(accessToken: string, id: number): Promise<void> {
  const res = await fetch(`/api/admin/articles/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
}

// Partner self-service: the same set, but scoped to the caller's partner.
// All four accept the optional `as` admin-impersonation id; non-admins
// pass undefined.
export async function listMyArticles(accessToken: string, as?: number): Promise<Article[]> {
  const res = await fetch(withAs('/api/me/partner/articles', as), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ articles: Article[] }>(res);
  return body.articles ?? [];
}

export async function createMyArticle(
  accessToken: string,
  payload: ArticlePayload,
  as?: number,
): Promise<Article> {
  const res = await fetch(withAs('/api/me/partner/articles', as), {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Article>(res);
}

export async function updateMyArticle(
  accessToken: string,
  id: number,
  payload: ArticlePayload,
  as?: number,
): Promise<Article> {
  const res = await fetch(withAs(`/api/me/partner/articles/${id}`, as), {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Article>(res);
}

export async function deleteMyArticle(accessToken: string, id: number, as?: number): Promise<void> {
  const res = await fetch(withAs(`/api/me/partner/articles/${id}`, as), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
}

// ─── Partner contributions ──────────────────────────────────────────────

export type ContribKind = 'litres' | 'article_stock';
export type ContribState = 'pending' | 'committed' | 'cancelled';

export type Contribution = {
  id: number;
  partnerId: number;
  kind: ContribKind;
  articleId?: number;
  articleTitle?: string;
  litres?: number;
  stockCount?: number;
  note?: string;
  createdByUserId: string;
  createdAt: string;
  committedAt: string;
  cancelledAt?: string;
  state: ContribState;
  cancellable: boolean;
};

export type ContributionPayload = {
  kind: ContribKind;
  articleId?: number;
  litres?: number;
  stockCount?: number;
  note?: string;
};

export async function listMyContributions(accessToken: string, as?: number): Promise<Contribution[]> {
  const res = await fetch(withAs('/api/me/partner/contributions', as), {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ contributions: Contribution[] }>(res);
  return body.contributions ?? [];
}

export async function createMyContribution(
  accessToken: string,
  payload: ContributionPayload,
  as?: number,
): Promise<Contribution> {
  const res = await fetch(withAs('/api/me/partner/contributions', as), {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Contribution>(res);
}

export async function cancelMyContribution(accessToken: string, id: number, as?: number): Promise<void> {
  const res = await fetch(withAs(`/api/me/partner/contributions/${id}`, as), {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
}

export async function commitMyContribution(accessToken: string, id: number, as?: number): Promise<Contribution> {
  const res = await fetch(withAs(`/api/me/partner/contributions/${id}/commit`, as), {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<Contribution>(res);
}

export async function listAdminContributions(
  accessToken: string,
  filter: { partnerId?: number; kind?: ContribKind; state?: ContribState; limit?: number } = {},
): Promise<Contribution[]> {
  const qs = new URLSearchParams();
  if (filter.partnerId) qs.set('partnerId', String(filter.partnerId));
  if (filter.kind) qs.set('kind', filter.kind);
  if (filter.state) qs.set('state', filter.state);
  if (filter.limit) qs.set('limit', String(filter.limit));
  const url = `/api/admin/partner-contributions${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ contributions: Contribution[] }>(res);
  return body.contributions ?? [];
}

export async function cancelAdminContribution(accessToken: string, id: number): Promise<void> {
  const res = await fetch(`/api/admin/partner-contributions/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
}

// ─── Leaderboard prizes ──────────────────────────────────────────────────

// scope is the literal 'global' (Switzerland-wide) OR a 2-letter canton.
export type PrizeScope = string;

export type LeaderboardPrize = {
  id: number;
  tournamentId: number;
  scope: PrizeScope;
  position: number;
  articleId: number;
  articleTitle: string;
  articleKind: ArticleKind;
  articleImage?: string;
  partnerName?: string;
  valueChf?: number;
  notes?: string;
};

export async function listLeaderboardPrizes(accessToken: string): Promise<LeaderboardPrize[]> {
  const res = await fetch('/api/admin/leaderboard-prizes', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ prizes: LeaderboardPrize[] }>(res);
  return body.prizes ?? [];
}

export async function upsertLeaderboardPrize(
  accessToken: string,
  payload: { scope: PrizeScope; position: number; articleId: number; notes?: string },
): Promise<LeaderboardPrize> {
  const res = await fetch('/api/admin/leaderboard-prizes', {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<LeaderboardPrize>(res);
}

export async function deleteLeaderboardPrize(accessToken: string, id: number): Promise<void> {
  const res = await fetch(`/api/admin/leaderboard-prizes/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
}

// ─── Winners (end-of-tournament payout list) ─────────────────────────────

export type Winner = {
  rank: number;
  userId: string;
  nickname: string;
  email: string;
  locale: string;
  canton?: string;
  pts: number;
  picks: number;
  prizeTitle?: string;
  prizeValueChf?: number;
  prizeStock?: number;
  prizePartner?: string;
  prizeNotes?: string;
};

export async function listWinners(
  accessToken: string,
  opts?: { tournament?: string; limit?: number },
): Promise<Winner[]> {
  const q = new URLSearchParams();
  if (opts?.tournament) q.set('tournament', opts.tournament);
  if (opts?.limit) q.set('limit', String(opts.limit));
  const res = await fetch(`/api/admin/winners${q.toString() ? `?${q}` : ''}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ winners: Winner[] }>(res);
  return body.winners ?? [];
}

/** Download the winners payout list as an .xlsx workbook. The known columns
 *  (rank, nickname, email, prize, …) are pre-filled; the contact columns
 *  (name, lastname, street, phone, place, cap, who will retrieve) come
 *  through blank, to complete by hand as winners reply. A plain <a href>
 *  can't carry the Bearer token, so we fetch the blob and click a synthetic
 *  link. */
export async function downloadWinnersXlsx(
  accessToken: string,
  opts?: { tournament?: string; limit?: number },
): Promise<void> {
  const q = new URLSearchParams();
  if (opts?.tournament) q.set('tournament', opts.tournament);
  if (opts?.limit) q.set('limit', String(opts.limit));
  const res = await fetch(`/api/admin/winners.xlsx${q.toString() ? `?${q}` : ''}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const body = await res.json();
      msg = body?.error?.message || msg;
    } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition') || '';
  const m = /filename="?([^";]+)"?/.exec(cd);
  const filename = m?.[1] || 'beercup-winners.xlsx';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ─── Waitlist ────────────────────────────────────────────────────────────

export type WaitlistEntry = {
  id: number;
  email: string;
  source: string;
  locale: string;
  ip?: string;
  userAgent?: string;
  referer?: string;
  createdAt: string;
};

export type WaitlistFilter = {
  search?: string;
  source?: string;
  locale?: string;
  limit?: number;
  offset?: number;
};

export type WaitlistListResponse = {
  entries: WaitlistEntry[];
  sources: string[];
  total: number;
  limit: number;
  offset: number;
};

export async function listAdminWaitlist(
  accessToken: string,
  filter: WaitlistFilter = {},
): Promise<WaitlistListResponse> {
  const qs = new URLSearchParams();
  if (filter.search) qs.set('search', filter.search);
  if (filter.source) qs.set('source', filter.source);
  if (filter.locale) qs.set('locale', filter.locale);
  if (filter.limit != null) qs.set('limit', String(filter.limit));
  if (filter.offset != null) qs.set('offset', String(filter.offset));
  const url = `/api/admin/waitlist${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
  return handle<WaitlistListResponse>(res);
}

// ─── Newsletter ──────────────────────────────────────────────────────────

// Admin-composed content shared by preview, test send and real send.
export type NewsletterContent = {
  subject: string;
  eyebrow?: string;
  title: string;
  body: string;
  buttonLabel?: string;
  buttonUrl?: string;
  footerNote?: string;
};

export type NewsletterAudience = 'waitlist' | 'users' | 'both';

export type NewsletterTestResult = {
  sent: number;
  failed: number;
  errors: string[] | null;
};

export type NewsletterSendResult = {
  id: number;
  recipients: number;
  sent: number;
  failed: number;
};

export type NewsletterSend = {
  id: number;
  subject: string;
  title: string;
  audience: NewsletterAudience;
  locales: string;
  recipients: number;
  sent: number;
  failed: number;
  createdAt: string;
};

/** Render the composed newsletter to HTML for the live preview iframe. */
export async function previewNewsletter(
  accessToken: string,
  content: NewsletterContent,
  locale: string,
): Promise<{ html: string; subject: string }> {
  const res = await fetch('/api/admin/newsletters/preview', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ ...content, locale }),
  });
  return handle<{ html: string; subject: string }>(res);
}

/** Send the newsletter to a few explicit addresses (synchronous). */
export async function testNewsletter(
  accessToken: string,
  content: NewsletterContent,
  locale: string,
  testEmails: string[],
): Promise<NewsletterTestResult> {
  const res = await fetch('/api/admin/newsletters/test', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ ...content, locale, testEmails }),
  });
  return handle<NewsletterTestResult>(res);
}

/** How many addresses the chosen audience + locale filter resolves to. */
export async function newsletterRecipientCount(
  accessToken: string,
  audience: NewsletterAudience,
  locales: string[],
): Promise<number> {
  const qs = new URLSearchParams({ audience });
  if (locales.length) qs.set('locales', locales.join(','));
  const res = await fetch(`/api/admin/newsletters/recipients?${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ count: number }>(res);
  return body.count;
}

/** Fire the real blast. Returns immediately; sending runs in the background. */
export async function sendNewsletter(
  accessToken: string,
  content: NewsletterContent,
  audience: NewsletterAudience,
  locales: string[],
  locale: string,
): Promise<NewsletterSendResult> {
  const res = await fetch('/api/admin/newsletters', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ ...content, audience, locales, locale }),
  });
  return handle<NewsletterSendResult>(res);
}

export async function listNewsletters(
  accessToken: string,
): Promise<NewsletterSend[]> {
  const res = await fetch('/api/admin/newsletters', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ sends: NewsletterSend[] }>(res);
  return body.sends ?? [];
}

// ─── Comments (per-match chat moderation) ────────────────────────────────

export type FlagReason = 'url' | 'profanity' | 'burst' | 'duplicate' | string;

export type AdminComment = {
  id: number;
  matchNumber: number;
  userId: string;
  nickname?: string;
  canton?: string;
  avatarUrl?: string;
  body: string;
  reviewed: boolean;
  flagged: boolean;
  flagReasons?: FlagReason[];
  createdAt: string;
};

export type AdminCommentsFilter = {
  reviewed?: boolean; // omit for both buckets
  flaggedOnly?: boolean;
  matchNumber?: number;
  limit?: number;
  offset?: number;
};

export type AdminCommentsResponse = {
  comments: AdminComment[];
  total: number;
};

export async function listAdminComments(
  accessToken: string,
  filter: AdminCommentsFilter = {},
): Promise<AdminCommentsResponse> {
  const qs = new URLSearchParams();
  if (filter.reviewed !== undefined) qs.set('reviewed', filter.reviewed ? '1' : '0');
  if (filter.flaggedOnly) qs.set('flagged', '1');
  if (filter.matchNumber != null) qs.set('matchNumber', String(filter.matchNumber));
  if (filter.limit != null) qs.set('limit', String(filter.limit));
  if (filter.offset != null) qs.set('offset', String(filter.offset));
  const url = `/api/admin/comments${qs.toString() ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } });
  return handle<AdminCommentsResponse>(res);
}

export async function setCommentReviewed(
  accessToken: string,
  id: number,
  reviewed: boolean,
): Promise<AdminComment> {
  const res = await fetch(`/api/admin/comments/${id}`, {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify({ reviewed }),
  });
  return handle<AdminComment>(res);
}

export async function deleteAdminComment(accessToken: string, id: number): Promise<void> {
  const res = await fetch(`/api/admin/comments/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok && res.status !== 204) throw new Error(`${res.status}`);
}

// ─── Global settings (chat maintenance toggle, etc.) ─────────────────────

export type AppSettings = {
  comments: {
    enabled: boolean;
    disabledMessage?: string;
  };
  /** Tournament code of the optional "second tournament" surfaced as a
   *  matchday section on the mobile Play tab + as a toggle on the
   *  leaderboard. Absent / empty = off. */
  testTournamentCode?: string;
};

export async function getAdminSettings(accessToken: string): Promise<AppSettings> {
  const res = await fetch('/api/admin/settings', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<AppSettings>(res);
}

export type AppSettingsUpdate = {
  comments?: {
    enabled?: boolean;
    disabledMessage?: string;
  };
  /** Set to a tournament code to enable the test section; pass an empty
   *  string to disable. */
  testTournamentCode?: string;
};

export async function putAdminSettings(
  accessToken: string,
  payload: AppSettingsUpdate,
): Promise<AppSettings> {
  const res = await fetch('/api/admin/settings', {
    method: 'PUT',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<AppSettings>(res);
}

// ─── Test tournament admin actions (Serie A 2025/26) ─────────────────────

/** Result of `POST /api/admin/test-tournament/seed` — the full Serie A
 *  season import. Same shape as the seed-seriea CLI summary. */
export type SeedTestTournamentResult = {
  leagueId: number;
  season: number;
  tournamentCode: string;
  teamsInserted: number;
  teamsTotal: number;
  matchesTotal: number;
  matchesUpserted: number;
  eventsInserted: number;
  eventsUpdated: number;
  eventsRetracted: number;
  statsUpserted: number;
  lineupsFetched: number;
  errors: number;
  dailyRemaining: number;
  minuteRemaining: number;
};

export async function seedTestTournament(
  accessToken: string,
  opts?: { includeDone?: boolean; maxDone?: number },
): Promise<SeedTestTournamentResult> {
  const qs: string[] = [];
  if (opts?.includeDone === false) qs.push('include_done=0');
  if (opts?.maxDone && opts.maxDone > 0) qs.push(`max_done=${opts.maxDone}`);
  const url = `/api/admin/test-tournament/seed${qs.length ? `?${qs.join('&')}` : ''}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<SeedTestTournamentResult>(res);
}

/** Result shape of the test-tournament tick — same TickSummary the
 *  internal sync endpoint returns. */
export type TestTournamentTickResult = {
  skipped?: string;
  reqs: number;
  liveMatches: number;
  matchesUpdated: number;
  eventsInserted: number;
  eventsUpdated: number;
  eventsRetracted: number;
  statsUpdated: number;
  lineupsFetched: number;
  errors?: string[];
  dailyRemaining?: number;
  minuteRemaining?: number;
};

export async function syncTestTournamentTick(
  accessToken: string,
): Promise<TestTournamentTickResult> {
  const res = await fetch('/api/admin/test-tournament/sync-tick', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<TestTournamentTickResult>(res);
}

export async function refreshTestTournamentFixtures(
  accessToken: string,
): Promise<RefreshSummary> {
  const res = await fetch('/api/admin/test-tournament/refresh-fixtures', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<RefreshSummary>(res);
}

/** Result of `POST /api/admin/test-tournament/backfill` — the targeted
 *  per-match detail pull for matches whose kickoff was in the last N days. */
export type BackfillTestTournamentResult = {
  tournamentCode: string;
  matchesScanned: number;
  eventsInserted: number;
  eventsUpdated: number;
  eventsRetracted: number;
  statsUpserted: number;
  lineupsFetched: number;
  errors: number;
  dailyRemaining: number;
  minuteRemaining: number;
};

export async function backfillTestTournament(
  accessToken: string,
  days = 2,
): Promise<BackfillTestTournamentResult> {
  const res = await fetch(`/api/admin/test-tournament/backfill?days=${days}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<BackfillTestTournamentResult>(res);
}

/** Upload a file via the admin uploads endpoint. Returns the public URL
 *  hosted on the configured GCS bucket. Use the URL as the `logo_path`
 *  on partner records (or any other admin-managed image). */
export async function uploadFile(
  accessToken: string,
  file: File,
  prefix = 'uploads',
): Promise<UploadResponse> {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('prefix', prefix);
  const res = await fetch('/api/admin/uploads', {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: fd,
  });
  return handle<UploadResponse>(res);
}

// ─── Leaderboard view + bulk recalc ───────────────────────────────────────

export type LeaderboardEntry = {
  rank: number;
  userId: string;
  nickname: string;
  cap: string;
  avatarUrl?: string;
  canton?: string;
  pts: number;
  picks: number;
  me?: boolean;
};

export type LeaderboardView = {
  scope: string;
  code?: string;
  total: number;
  limit: number;
  offset: number;
  entries: LeaderboardEntry[];
  me?: LeaderboardEntry;
};

/** GET /api/leaderboard?scope=swiss|canton[&code=TI]&tournament=... */
export async function getLeaderboard(
  accessToken: string,
  opts: { tournament: string; scope: 'swiss' | 'canton'; code?: string; limit?: number; offset?: number; search?: string },
): Promise<LeaderboardView> {
  const qs = new URLSearchParams({
    tournament: opts.tournament,
    scope: opts.scope,
    limit: String(opts.limit ?? 100),
    offset: String(opts.offset ?? 0),
  });
  if (opts.code) qs.set('code', opts.code);
  if (opts.search) qs.set('search', opts.search);
  const res = await fetch(`/api/leaderboard?${qs}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return handle<LeaderboardView>(res);
}

export type RecalcAllResult = {
  tournamentCode: string;
  matchesWalked: number;
  scored: number;
  leaguesScored: number;
  vouchersIssued: number;
  errors?: string[];
};

/** POST /api/admin/tournaments/{code}/recalc-all */
export async function recalcAllForTournament(
  accessToken: string,
  code: string,
): Promise<RecalcAllResult> {
  const res = await fetch(`/api/admin/tournaments/${encodeURIComponent(code)}/recalc-all`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<RecalcAllResult>(res);
}

export type RefreshRankResult = {
  tournamentCode: string;
  snapshotsDropped: number;
};

/** POST /api/admin/tournaments/{code}/refresh-rankings */
export async function refreshRankings(
  accessToken: string,
  code: string,
): Promise<RefreshRankResult> {
  const res = await fetch(`/api/admin/tournaments/${encodeURIComponent(code)}/refresh-rankings`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<RefreshRankResult>(res);
}

export type LinkEuropeanFinalsResult = {
  tournament: string;
  finals: Record<string, {
    leagueId: number;
    linked: boolean;
    apiFixtureId?: number;
    apiTeamHomeId?: number;
    apiTeamAwayId?: number;
    homeName?: string;
    awayName?: string;
    kickoffUtc?: string;
    error?: string;
  }>;
};

/** POST /api/admin/european-finals/link */
export async function linkEuropeanFinals(
  accessToken: string,
): Promise<LinkEuropeanFinalsResult> {
  const res = await fetch('/api/admin/european-finals/link', {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<LinkEuropeanFinalsResult>(res);
}

// ─── Broadcast messages ───────────────────────────────────────────────────

export type Broadcast = {
  id: number;
  title: string;
  body: string;
  /** 'info' = generic announcement, 'update' = app-update nag (the mobile
   *  app shows a store button and gates on minVersion). */
  kind: 'info' | 'update';
  /** Only app builds *older* than this see the message. Absent = everyone. */
  minVersion?: string;
  active: boolean;
  createdAt: string;
  activatedAt?: string;
  deactivatedAt?: string;
};

/** GET /api/admin/broadcasts — full history, newest first. */
export async function listBroadcasts(accessToken: string): Promise<Broadcast[]> {
  const res = await fetch('/api/admin/broadcasts', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  const body = await handle<{ broadcasts: Broadcast[] }>(res);
  return body.broadcasts ?? [];
}

export type BroadcastCreatePayload = {
  title: string;
  body: string;
  kind: 'info' | 'update';
  minVersion?: string;
  /** Create-and-activate in one call (deactivates any other live one). */
  activate?: boolean;
};

/** POST /api/admin/broadcasts */
export async function createBroadcast(
  accessToken: string,
  payload: BroadcastCreatePayload,
): Promise<Broadcast> {
  const res = await fetch('/api/admin/broadcasts', {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  return handle<Broadcast>(res);
}

/** POST /api/admin/broadcasts/{id}/activate — flips every other one off. */
export async function activateBroadcast(
  accessToken: string,
  id: number,
): Promise<Broadcast> {
  const res = await fetch(`/api/admin/broadcasts/${id}/activate`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<Broadcast>(res);
}

/** POST /api/admin/broadcasts/{id}/deactivate */
export async function deactivateBroadcast(
  accessToken: string,
  id: number,
): Promise<Broadcast> {
  const res = await fetch(`/api/admin/broadcasts/${id}/deactivate`, {
    method: 'POST',
    headers: authHeaders(accessToken),
  });
  return handle<Broadcast>(res);
}
